import * as R from "ramda";

import { countries, subdivisions } from "../data";
import { getValueFromPath, getJsonSchemaFromPath } from "../util";
import { formatDatetimeString } from "./formatDateTimeString";
export const toTitleCase = (w?: string) =>
    w ? w.replace(/^./, w => w.toUpperCase()) : "";

export const normalize = (val: string): string => {
    const normalizeSnakeCase = R.replace(/_/g, " ");
    const normalizeCamelCase = R.replace(/([A-Z][a-z]+)/g, " $1");
    return R.pipe(
        normalizeSnakeCase,
        normalizeCamelCase,
        R.split(" "),
        R.map(R.trim),
        R.map(toTitleCase),
        R.join(" ")
    )(val);
};

export const countryIsoCodeToName = (
    isoCode: string = "",
    defaultName: string = "Unknown",
    ignoreEmpty?: boolean
) => R.propOr(ignoreEmpty ? "" : defaultName, R.toUpper(isoCode), countries);

export const subdivisionIsoCodeToName = (
    isoCode: string = "",
    defaultName: string = "Unknown",
    ignoreEmpty?: boolean
) => R.propOr(ignoreEmpty ? "" : defaultName, R.toUpper(isoCode), subdivisions);

export const createNameToLabelMappingFromFieldSchema = R.pipe(
    R.pathOr([], ["options"]),
    R.juxt([R.pluck("value"), R.pluck("label")]),
    R.apply(R.zipObj)
);

const _getDataAndSchema = (
    jsonSchema: Object,
    data: any,
    path: any,
    separator: string
) => {
    return path && typeof path === "string"
        ? [
              getJsonSchemaFromPath(path, jsonSchema, separator),
              getValueFromPath(path, data, separator),
          ]
        : [jsonSchema, data];
};

const isEmpty = (value: any, schema: JsonSchema): boolean => {
    if (value === null) {
        return true;
    }
    if (schema.type === "string") {
        const empty = value === "";
        if (empty) {
            return empty;
        }
        if ("options" in schema) {
            const defaultOption = schema.options.find(
                (el: { value: any; isDefault: boolean }) => el.isDefault
            );
            return Boolean(defaultOption && defaultOption.value === value);
        }
        return false;
    }
    if (schema.type === "object") {
        return Object.keys(value).length === 0;
    }
    if (schema.type === "array") {
        return value.length === 0;
    }
    if (schema.type === "integer" || schema.type === "number") {
        return value === 0;
    }
    return false;
};

export const filterHiddenValues = ({
    jsonSchema,
    data,
    isHidden = (schemaSlice: JsonSchema) => Boolean(schemaSlice.isHidden),
}: {
    jsonSchema: JsonSchema;
    data: any;
    isHidden?: (schemaSlice: JsonSchema) => boolean;
}): any | null => {
    if (isHidden(jsonSchema)) {
        return null;
    }
    if (jsonSchema.type === "array") {
        const { items: elementSchema } = jsonSchema;
        return (data as Array<any>).reduce((acc, item) => {
            const nestedResult = filterHiddenValues({
                jsonSchema: elementSchema,
                data: item,
            });
            if (nestedResult !== null) {
                acc.push(nestedResult);
            }
            return acc;
        }, []);
    } else if (jsonSchema.type === "object") {
        return Object.entries(data).reduce((acc: any, [key, value]) => {
            const propertySchema = jsonSchema.properties[key];
            const shouldAdd = !isHidden(propertySchema);
            if (shouldAdd) {
                const nested = filterHiddenValues({
                    jsonSchema: propertySchema,
                    data: value,
                    isHidden,
                });
                acc[key] = nested;
            }
            return acc;
        }, {});
    } else {
        return data;
    }
};

export const filterEmptyValues = ({
    jsonSchema,
    data,
}: {
    jsonSchema: JsonSchema;
    data: any;
}): any | null => {
    if (jsonSchema.type === "array") {
        const elementSchema = jsonSchema.items;
        const result = (data as Array<any>).reduce((acc, item) => {
            const nestedResult = filterEmptyValues({
                jsonSchema: elementSchema,
                data: item,
            });
            const shouldAdd = !isEmpty(nestedResult, elementSchema);
            if (shouldAdd) {
                acc.push(nestedResult);
            }
            return acc;
        }, []);
        return isEmpty(result, jsonSchema) ? null : result;
    } else if (jsonSchema.type === "object") {
        const result = Object.entries(data).reduce((acc: any, [key, value]) => {
            const propertySchema = jsonSchema.properties[key];
            const nested = filterEmptyValues({
                jsonSchema: propertySchema,
                data: value,
            });
            const empty = isEmpty(nested, propertySchema);
            if (!empty) {
                acc[key] = nested;
            }
            return acc;
        }, {});
        return isEmpty(result, jsonSchema) ? null : result;
    } else {
        // primitives
        return isEmpty(data, jsonSchema) ? null : data;
    }
};

export const formatWithJsonSchema = ({
    jsonSchema,
    data,
    path = "",
    separator = ".",
    ignoreEmpty = false,
    parentObject,
    fieldName = "",
    object = {},
}: {
    data: any;
    jsonSchema: JsonSchema;
    path?: string;
    separator?: string;
    ignoreEmpty?: boolean;
    parentObject?: any;
    fieldName?: string;
    object?: any;
}): any => {
    const [currentSchema, currentData] = _getDataAndSchema(
        jsonSchema,
        data,
        path,
        separator
    );

    if (R.isNil(currentSchema) || R.isEmpty(currentSchema)) {
        return currentData;
    }

    if (R.type(currentData) == "Object") {
        return R.mapObjIndexed(
            (val, key) =>
                formatWithJsonSchema({
                    jsonSchema: R.pathOr(
                        {},
                        ["properties", key],
                        currentSchema
                    ),
                    data: val,
                    ignoreEmpty,
                    parentObject: currentData,
                    fieldName: key,
                    object: currentData,
                }),
            currentData
        );
    } else if (R.type(currentData) == "Array") {
        return R.map(
            val =>
                formatWithJsonSchema({
                    data: val as any,
                    jsonSchema: R.pathOr({}, ["items"], currentSchema) as any,
                    ignoreEmpty,
                }),
            currentData
        );
    } else {
        const _data = R.has("enum", currentSchema)
            ? R.pipe(
                  createNameToLabelMappingFromFieldSchema,
                  R.pathOr(currentData, [currentData])
              )(currentSchema)
            : currentData;

        const isSchemaTypeString = R.equals(currentSchema.type, "string");

        if (isSchemaTypeString) {
            if (typeof _data === "string") {
                if (currentSchema.isSelect)
                    return R.has("select", currentSchema)
                        ? R.pipe(
                              createNameToLabelMappingFromFieldSchema,
                              R.pathOr(currentData, [currentData])
                          )(currentSchema)
                        : currentData;
                if (currentSchema.isCountryCode)
                    return countryIsoCodeToName(_data, undefined, ignoreEmpty);
                if (currentSchema.isState) {
                    if (!parentObject || parentObject.countryCode === undefined)
                        throw "Expecting parentObject to have countryCode field";
                    return subdivisionIsoCodeToName(
                        `${parentObject.countryCode}-${_data}`,
                        _data,
                        ignoreEmpty
                    );
                }
            }
            if (currentSchema.isDate) {
                const datePrecisionKey = `${fieldName}Precision`;
                return formatDatetimeString({
                    str: _data,
                    precision: object[datePrecisionKey],
                });
            }
        }
        return _data;
    }
};
